import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Hand, Sparkles, Star, Moon, Hash, Lock, ArrowRight, LogIn, LogOut, User, Crown, Music, Volume2, Activity, Sun, Compass, HelpCircle, UserPlus, Calendar, Wand2, Quote, MessageCircle, Send, Edit3, Trash2, X, Loader2 } from 'lucide-react';
import { useAuth } from '@/components/AuthContext';
import PersonalDashboard from '@/components/PersonalDashboard';
import PlaylistToggle from '@/components/PlaylistToggle';
import Footer from '@/components/Footer';
import { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// App icons mapping
const APP_ICONS = {
  LUMINA: Hand,
  MYSTERIAE: Moon,
  ASTROZODIAC: Sun,
  NUMEROLOGIA: Hash,
  REVELIAE: Sparkles
};

// Avatar colors for reviews
const AVATAR_COLORS = [
  'from-purple-400 to-purple-600',
  'from-blue-400 to-blue-600',
  'from-emerald-400 to-emerald-600',
  'from-amber-400 to-amber-600',
  'from-rose-400 to-rose-600',
  'from-cyan-400 to-cyan-600'
];

// Default testimonials (fallback when no real reviews exist)
const DEFAULT_TESTIMONIALS = [
  {
    review_id: 'default_1',
    display_name: 'S.',
    first_name: 'Sophie',
    rating: 5,
    content: "L'analyse de ma main par LUMINA m'a bluffee ! Les predictions etaient d'une precision etonnante. J'utilise maintenant l'horoscope quotidien chaque matin.",
    app_used: 'LUMINA',
    is_premium: false
  },
  {
    review_id: 'default_2',
    display_name: 'T.',
    first_name: 'Thomas',
    rating: 5,
    content: "MYSTERIAE est devenu mon oracle quotidien. Les tirages de tarot sont profonds et les interpretations de l'IA sont vraiment pertinentes. Un must-have !",
    app_used: 'MYSTERIAE',
    is_premium: false
  },
  {
    review_id: 'default_3',
    display_name: 'M.',
    first_name: 'Marie',
    rating: 4,
    content: "Grace a NUMEROLOGIA, j'ai decouvert mon chemin de vie. L'analyse transversale Premium combine tout et m'aide a mieux me comprendre. Vraiment impressionnant !",
    app_used: 'NUMEROLOGIA',
    is_premium: true
  }
];

// Star Rating Component
const StarRating = ({ rating, interactive = false, onChange, size = 'md' }) => {
  const [hoverRating, setHoverRating] = useState(0);
  const sizeClasses = {
    sm: 'h-3.5 w-3.5',
    md: 'h-5 w-5',
    lg: 'h-6 w-6'
  };
  
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={!interactive}
          onClick={() => interactive && onChange && onChange(star)}
          onMouseEnter={() => interactive && setHoverRating(star)}
          onMouseLeave={() => interactive && setHoverRating(0)}
          className={`${interactive ? 'cursor-pointer hover:scale-110' : 'cursor-default'} transition-transform`}
        >
          <Star
            className={`${sizeClasses[size]} ${
              star <= (hoverRating || rating)
                ? 'fill-amber-400 text-amber-400'
                : 'fill-gray-200 text-gray-200'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

// Review Card Component
const ReviewCard = ({ review, index, isAdmin, onReply }) => {
  const AppIcon = APP_ICONS[review.app_used] || Sparkles;
  const avatarColor = AVATAR_COLORS[index % AVATAR_COLORS.length];
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const handleSubmitReply = async () => {
    if (replyContent.length < 5) return;
    setIsSubmitting(true);
    await onReply(review.review_id, replyContent);
    setShowReplyForm(false);
    setReplyContent('');
    setIsSubmitting(false);
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 * index }}
      className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm hover:shadow-lg hover:border-amber-200 transition-all"
    >
      <div className="flex items-start gap-3 mb-4">
        <div className={`w-12 h-12 bg-gradient-to-br ${avatarColor} rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0`}>
          {review.first_name?.[0]?.toUpperCase() || review.display_name?.[0]?.toUpperCase() || 'U'}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h4 className="font-semibold text-gray-900">{review.first_name || 'Utilisateur'}</h4>
            {review.is_premium && (
              <Crown className="h-4 w-4 text-amber-500" />
            )}
          </div>
          <StarRating rating={review.rating} size="sm" />
        </div>
      </div>
      <Quote className="h-6 w-6 text-amber-300 mb-2" />
      <p className="text-gray-600 leading-relaxed">{review.content}</p>
      <div className="mt-4 flex items-center gap-2">
        <AppIcon className="h-4 w-4 text-amber-500" />
        <span className="text-xs text-gray-500">
          {review.is_premium ? 'Utilisateur Premium' : `Utilisateur de ${review.app_used}`}
        </span>
      </div>
      
      {/* Reply Section */}
      {review.reply && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mt-4 pt-4 border-t border-gray-100"
        >
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-violet-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-semibold text-purple-700 text-sm">{review.reply.admin_name || "L'equipe REVELIAE"}</span>
                <span className="px-2 py-0.5 bg-purple-100 text-purple-600 rounded-full text-xs">Equipe</span>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed">{review.reply.content}</p>
            </div>
          </div>
        </motion.div>
      )}
      
      {/* Admin Reply Button */}
      {isAdmin && !review.reply && !showReplyForm && (
        <div className="mt-4 pt-3 border-t border-gray-100">
          <button
            onClick={() => setShowReplyForm(true)}
            className="flex items-center gap-2 text-sm text-purple-600 hover:text-purple-700 font-medium"
          >
            <MessageCircle className="h-4 w-4" />
            Repondre
          </button>
        </div>
      )}
      
      {/* Reply Form for Admin */}
      <AnimatePresence>
        {showReplyForm && isAdmin && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-4 pt-4 border-t border-gray-100 overflow-hidden"
          >
            <div className="space-y-3">
              <textarea
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value.slice(0, 500))}
                placeholder="Votre reponse..."
                rows={3}
                className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:border-purple-400 focus:ring-0 resize-none"
              />
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-400">{replyContent.length}/500</span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowReplyForm(false)}
                    className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleSubmitReply}
                    disabled={replyContent.length < 5 || isSubmitting}
                    className="px-4 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-1"
                  >
                    {isSubmitting ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <>
                        <Send className="h-3 w-3" />
                        Envoyer
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Review Form Component
const ReviewForm = ({ onSubmit, onCancel, existingReview, isLoading }) => {
  const [rating, setRating] = useState(existingReview?.rating || 5);
  const [content, setContent] = useState(existingReview?.content || '');
  const [appUsed, setAppUsed] = useState(existingReview?.app_used || 'REVELIAE');
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (content.length < 10) return;
    onSubmit({ rating, content, app_used: appUsed });
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl border-2 border-amber-200 p-6 mb-6"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-900">
          {existingReview ? 'Modifier mon avis' : 'Laisser un avis'}
        </h3>
        <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
          <X className="h-5 w-5" />
        </button>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Rating */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Note</label>
          <StarRating rating={rating} interactive onChange={setRating} size="lg" />
        </div>
        
        {/* App Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Application utilisee</label>
          <div className="flex flex-wrap gap-2">
            {Object.keys(APP_ICONS).map((app) => {
              const Icon = APP_ICONS[app];
              return (
                <button
                  key={app}
                  type="button"
                  onClick={() => setAppUsed(app)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all ${
                    appUsed === app
                      ? 'border-amber-500 bg-amber-100 text-amber-800'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-amber-300'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span className="text-sm font-medium">{app}</span>
                </button>
              );
            })}
          </div>
        </div>
        
        {/* Content */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Votre avis <span className="text-gray-400">({content.length}/500)</span>
          </label>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value.slice(0, 500))}
            placeholder="Partagez votre experience avec REVELIAE..."
            rows={4}
            className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-amber-400 focus:ring-0 resize-none"
            required
            minLength={10}
          />
          {content.length > 0 && content.length < 10 && (
            <p className="text-xs text-red-500 mt-1">Minimum 10 caracteres</p>
          )}
        </div>
        
        {/* Submit */}
        <div className="flex gap-3">
          <Button
            type="submit"
            disabled={isLoading || content.length < 10}
            className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                {existingReview ? 'Mettre a jour' : 'Publier'}
              </>
            )}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel}>
            Annuler
          </Button>
        </div>
      </form>
    </motion.div>
  );
};

// Testimonials Section Component
const TestimonialsSection = ({ isAuthenticated, user }) => {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ total_reviews: 0, average_rating: 4.9 });
  const [myReview, setMyReview] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasCheckedMyReview, setHasCheckedMyReview] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Fetch featured reviews
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/reviews/featured`);
        if (response.data.success) {
          setReviews(response.data.reviews.length > 0 ? response.data.reviews : DEFAULT_TESTIMONIALS);
          setStats(response.data.stats || { total_reviews: 0, average_rating: 4.9 });
        }
      } catch (err) {
        console.error('Error fetching reviews:', err);
        setReviews(DEFAULT_TESTIMONIALS);
      }
    };
    fetchReviews();
  }, []);
  
  // Fetch user's own review and check admin status
  useEffect(() => {
    const fetchMyReview = async () => {
      if (!isAuthenticated) return;
      try {
        const response = await axios.get(`${API_URL}/api/reviews/my`, { withCredentials: true });
        if (response.data.success && response.data.review) {
          setMyReview(response.data.review);
        }
        setHasCheckedMyReview(true);
        
        // Check if user is admin
        const adminResponse = await axios.get(`${API_URL}/api/reviews/admin/check`, { withCredentials: true });
        if (adminResponse.data.success && adminResponse.data.is_admin) {
          setIsAdmin(true);
        }
      } catch (err) {
        console.error('Error fetching my review:', err);
        setHasCheckedMyReview(true);
      }
    };
    fetchMyReview();
  }, [isAuthenticated]);
  
  const handleSubmitReview = async (reviewData) => {
    setIsLoading(true);
    try {
      const endpoint = myReview 
        ? `${API_URL}/api/reviews`
        : `${API_URL}/api/reviews`;
      const method = myReview ? 'put' : 'post';
      
      const response = await axios[method](endpoint, reviewData, { withCredentials: true });
      
      if (response.data.success) {
        setMyReview(response.data.review);
        setShowForm(false);
        // Refresh reviews
        const refreshResponse = await axios.get(`${API_URL}/api/reviews/featured`);
        if (refreshResponse.data.success) {
          setReviews(refreshResponse.data.reviews.length > 0 ? refreshResponse.data.reviews : DEFAULT_TESTIMONIALS);
          setStats(refreshResponse.data.stats);
        }
      }
    } catch (err) {
      console.error('Error submitting review:', err);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleDeleteReview = async () => {
    if (!confirm('Supprimer votre avis ?')) return;
    setIsLoading(true);
    try {
      const response = await axios.delete(`${API_URL}/api/reviews`, { withCredentials: true });
      if (response.data.success) {
        setMyReview(null);
        // Refresh reviews
        const refreshResponse = await axios.get(`${API_URL}/api/reviews/featured`);
        if (refreshResponse.data.success) {
          setReviews(refreshResponse.data.reviews.length > 0 ? refreshResponse.data.reviews : DEFAULT_TESTIMONIALS);
          setStats(refreshResponse.data.stats);
        }
      }
    } catch (err) {
      console.error('Error deleting review:', err);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleReply = async (reviewId, content) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/reviews/${reviewId}/reply`,
        { content },
        { withCredentials: true }
      );
      
      if (response.data.success) {
        // Update the review in local state
        setReviews(prev => prev.map(r => 
          r.review_id === reviewId 
            ? { ...r, reply: response.data.reply, has_reply: true }
            : r
        ));
      }
    } catch (err) {
      console.error('Error adding reply:', err);
    }
  };
  
  return (
    <div className="max-w-6xl mx-auto px-4 pb-16">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        {/* Section Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-full mb-4">
            <MessageCircle className="h-4 w-4 text-amber-600" />
            <span className="text-sm font-medium text-amber-700">Temoignages</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
            Ce que disent nos utilisateurs
          </h2>
          <p className="text-gray-600 max-w-xl mx-auto">
            Decouvrez les experiences de ceux qui ont explore les mysteres avec REVELIAE
          </p>
        </div>
        
        {/* Review Form or CTA */}
        <AnimatePresence>
          {showForm && isAuthenticated ? (
            <ReviewForm
              onSubmit={handleSubmitReview}
              onCancel={() => setShowForm(false)}
              existingReview={myReview}
              isLoading={isLoading}
            />
          ) : isAuthenticated && hasCheckedMyReview ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mb-6 flex justify-center"
            >
              {myReview ? (
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-600">Vous avez deja laisse un avis</span>
                  <Button
                    onClick={() => setShowForm(true)}
                    variant="outline"
                    size="sm"
                    className="border-amber-300 text-amber-700 hover:bg-amber-50"
                  >
                    <Edit3 className="h-4 w-4 mr-1" />
                    Modifier
                  </Button>
                  <Button
                    onClick={handleDeleteReview}
                    variant="outline"
                    size="sm"
                    className="border-red-300 text-red-600 hover:bg-red-50"
                    disabled={isLoading}
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Supprimer
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={() => setShowForm(true)}
                  className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                  data-testid="leave-review-btn"
                >
                  <MessageCircle className="h-4 w-4 mr-2" />
                  Laisser un avis
                </Button>
              )}
            </motion.div>
          ) : null}
        </AnimatePresence>
        
        {/* Reviews Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {reviews.slice(0, 6).map((review, index) => (
            <ReviewCard 
              key={review.review_id} 
              review={review} 
              index={index}
              isAdmin={isAdmin}
              onReply={handleReply}
            />
          ))}
        </div>
        
        {/* Stats Row */}
        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 text-center border border-amber-100">
            <p className="text-3xl font-bold text-amber-600">
              {stats.total_reviews > 0 ? `${stats.total_reviews}+` : '10k+'}
            </p>
            <p className="text-sm text-gray-600">
              {stats.total_reviews > 0 ? 'Avis verifies' : 'Utilisateurs actifs'}
            </p>
          </div>
          <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-4 text-center border border-purple-100">
            <p className="text-3xl font-bold text-purple-600">50k+</p>
            <p className="text-sm text-gray-600">Lectures realisees</p>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-4 text-center border border-blue-100">
            <p className="text-3xl font-bold text-blue-600">
              {stats.average_rating > 0 ? stats.average_rating.toFixed(1) : '4.8'}
            </p>
            <p className="text-sm text-gray-600">Note moyenne</p>
          </div>
          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-4 text-center border border-emerald-100">
            <p className="text-3xl font-bold text-emerald-600">98%</p>
            <p className="text-sm text-gray-600">Satisfaction</p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

const PRODUCTS = [
  {
    id: 'lumina',
    name: 'LUMINA',
    engine: 'Palm Engine',
    description: 'Lecture de lignes de la main par IA',
    icon: Hand,
    status: 'available',
    isFree: true,
    path: '/home',
    gradient: 'from-amber-500 to-orange-600',
    bgImage: 'https://customer-assets.emergentagent.com/job_00ae1b8c-29ea-4bfc-bef7-df5c6062792b/artifacts/x5ubhjmi_20260222_1656_Image%20Generation_remix_01kj30zprye119xnsr5eeca9q7.png'
  },
  {
    id: 'mysteriae',
    name: 'MYSTERIAE',
    engine: 'Tarot Engine',
    description: 'Explorez les 22 Arcanes Majeurs du Tarot',
    icon: Moon,
    status: 'available',
    isFree: false,
    path: '/mysteriae',
    gradient: 'from-purple-500 to-indigo-600'
  },
  {
    id: 'astrozodiac',
    name: 'ASTROZODIAC',
    engine: 'Astro Engine',
    description: 'Analyse astrologique personnalisée par IA',
    icon: Star,
    status: 'available',
    isFree: false,
    path: '/astrozodiac',
    gradient: 'from-blue-500 to-cyan-600'
  },
  {
    id: 'numerologia',
    name: 'NUMEROLOGIA',
    engine: 'Numerology Engine',
    description: 'Analyse numérologique par IA',
    icon: Hash,
    status: 'available',
    isFree: false,
    path: '/numerologia',
    gradient: 'from-emerald-500 to-teal-600'
  }
];

const ProductCard = ({ product, index }) => {
  const Icon = product.icon;
  const isAvailable = product.status === 'available';
  
  const cardContent = (
    <div className={`
      relative overflow-hidden rounded-3xl
      bg-gradient-to-br from-gray-900 via-gray-800 to-black
      border border-amber-500/20
      transition-all duration-500
      ${isAvailable ? 'hover:border-amber-500/60 hover:shadow-2xl hover:shadow-amber-500/20 hover:scale-105' : 'opacity-70'}
    `}>
      {/* Starry background effect */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-4 left-8 w-1 h-1 bg-amber-300 rounded-full animate-pulse" />
        <div className="absolute top-12 right-12 w-1.5 h-1.5 bg-amber-200 rounded-full animate-pulse delay-100" />
        <div className="absolute bottom-20 left-16 w-1 h-1 bg-amber-400 rounded-full animate-pulse delay-200" />
        <div className="absolute top-24 left-24 w-0.5 h-0.5 bg-white rounded-full animate-pulse delay-300" />
        <div className="absolute bottom-12 right-8 w-1 h-1 bg-amber-300 rounded-full animate-pulse delay-150" />
      </div>
      
      {/* Golden glow effect */}
      <div className={`absolute inset-0 bg-gradient-to-t from-amber-500/10 via-transparent to-transparent ${isAvailable ? 'group-hover:from-amber-500/20' : ''} transition-all duration-500`} />
      
      <div className="relative p-8 flex flex-col items-center text-center h-[340px]">
        {/* Status Badge */}
        <div className="absolute top-4 right-4">
          {product.isFree ? (
            <span className="px-3 py-1 text-xs font-bold bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-full shadow-lg">
              GRATUIT
            </span>
          ) : isAvailable ? (
            <span className="px-3 py-1 text-xs font-bold bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-full shadow-lg">
              PREMIUM
            </span>
          ) : (
            <span className="px-3 py-1 text-xs font-medium bg-gray-800/80 text-amber-400 rounded-full border border-amber-500/30">
              Bientôt
            </span>
          )}
        </div>
        
        {/* Icon with golden glow */}
        <div className={`
          relative mb-5 p-5 rounded-full
          bg-gradient-to-br ${product.gradient}
          shadow-lg ${isAvailable ? 'shadow-amber-500/30' : 'shadow-gray-700/30'}
        `}>
          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-amber-400/20 to-transparent" />
          <Icon className="h-10 w-10 text-white relative z-10" strokeWidth={1.5} />
          {!isAvailable && (
            <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40">
              <Lock className="h-5 w-5 text-amber-400" />
            </div>
          )}
        </div>
        
        {/* Engine Name */}
        <h3 className="font-heading text-lg text-amber-400 mb-1 tracking-wide">
          {product.engine}
        </h3>
        
        {/* Product Name */}
        <h2 className="font-heading text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-200 via-amber-400 to-amber-200 mb-3 tracking-wider">
          {product.name}
        </h2>
        
        {/* Description */}
        <p className="text-gray-400 text-sm leading-relaxed mb-4 max-w-[200px] h-[40px] flex items-center justify-center">
          {product.description}
        </p>
        
        {/* Action */}
        {isAvailable ? (
          <div className="mt-auto bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-black font-semibold rounded-full px-6 py-2 transition-all duration-300 group-hover:shadow-lg group-hover:shadow-amber-500/30 flex items-center">
            Explorer
            <ArrowRight className="ml-2 h-4 w-4" />
          </div>
        ) : (
          <div className="mt-auto px-6 py-2 text-amber-400/60 text-sm font-medium">
            Disponible prochainement
          </div>
        )}
      </div>
    </div>
  );
  
  // Use Link for available products, div for unavailable ones
  if (isAvailable && product.path) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: index * 0.1 }}
        className="relative group"
        data-testid={`product-card-${product.id}`}
      >
        <Link 
          to={product.path} 
          className="block cursor-pointer"
          data-testid={`explore-${product.id}-btn`}
        >
          {cardContent}
        </Link>
      </motion.div>
    );
  }
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="relative group cursor-not-allowed"
      data-testid={`product-card-${product.id}`}
    >
      {cardContent}
    </motion.div>
  );
};

const ReveliaePage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout, isLoading } = useAuth();

  const handleLogout = async () => {
    await logout();
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-amber-200/50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Sparkles className="h-6 w-6 text-amber-500" />
            <span className="font-heading text-2xl font-bold tracking-wider bg-gradient-to-r from-amber-600 via-amber-500 to-amber-600 bg-clip-text text-transparent">
              REVELIAE
            </span>
          </div>
          
          {!isLoading && (
            <div className="flex items-center gap-4">
              {isAuthenticated && user ? (
                <div className="flex items-center gap-3">
                  <Link 
                    to="/profile"
                    className="flex items-center gap-2 bg-amber-50 px-4 py-2 rounded-full border border-amber-200 hover:bg-amber-100 hover:border-amber-300 transition-all"
                    data-testid="profile-link"
                  >
                    <User className="h-4 w-4 text-amber-600" />
                    <span className="text-sm font-medium text-amber-800">{user.first_name || user.name}</span>
                  </Link>
                  <Button
                    data-testid="reveliae-logout-btn"
                    onClick={handleLogout}
                    variant="ghost"
                    size="sm"
                    className="rounded-full text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Déconnexion
                  </Button>
                </div>
              ) : (
                <Button
                  data-testid="reveliae-login-btn"
                  onClick={() => navigate('/auth')}
                  variant="outline"
                  size="sm"
                  className="rounded-full border-amber-400 text-amber-600 hover:bg-amber-50 hover:border-amber-500"
                >
                  <LogIn className="h-4 w-4 mr-2" />
                  Connexion
                </Button>
              )}
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <div className="pt-32 pb-8 px-4">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto"
        >
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
            <span className="bg-gradient-to-r from-amber-600 via-amber-500 to-amber-600 bg-clip-text text-transparent">
              Explorez les mystères
            </span>
            <span className="block text-gray-800 mt-2">de votre destinée</span>
          </h1>
          <p className="text-gray-600 text-lg max-w-xl mx-auto leading-relaxed">
            Une suite d'applications propulsées par l'IA pour explorer les arts divinatoires 
            avec sagesse et modernité.
          </p>
          
          {/* Try Free CTA for non-authenticated users */}
          {!isAuthenticated && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4"
            >
              <Link
                to="/try"
                className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white text-lg font-semibold rounded-full shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                data-testid="try-free-cta"
              >
                <Hand className="h-5 w-5" />
                Essayer gratuitement
                <ArrowRight className="h-5 w-5" />
              </Link>
              <span className="text-sm text-gray-500">
                Scannez votre paume en 30 secondes
              </span>
            </motion.div>
          )}
        </motion.div>
      </div>

      {/* How It Works Section */}
      <div className="max-w-6xl mx-auto px-4 mb-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 rounded-3xl border-2 border-amber-200 p-6 md:p-8 shadow-lg">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-full flex items-center justify-center shadow-md">
                <HelpCircle className="h-5 w-5 text-white" />
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900">Comment ca marche ?</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-6">
              {/* Step 1 */}
              <div className="relative">
                <div className="flex flex-col items-center text-center p-4 bg-white/70 rounded-2xl border border-amber-100 hover:border-amber-300 hover:shadow-md transition-all">
                  <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-amber-500 rounded-full flex items-center justify-center mb-3 shadow-sm">
                    <UserPlus className="h-6 w-6 text-white" />
                  </div>
                  <div className="absolute -top-2 -left-2 w-7 h-7 bg-amber-500 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md">
                    1
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">Creez votre compte</h3>
                  <p className="text-sm text-gray-600">
                    Inscrivez-vous gratuitement en quelques secondes
                  </p>
                </div>
                <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2">
                  <ArrowRight className="h-5 w-5 text-amber-400" />
                </div>
              </div>
              
              {/* Step 2 */}
              <div className="relative">
                <div className="flex flex-col items-center text-center p-4 bg-white/70 rounded-2xl border border-amber-100 hover:border-amber-300 hover:shadow-md transition-all">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-orange-500 rounded-full flex items-center justify-center mb-3 shadow-sm">
                    <Calendar className="h-6 w-6 text-white" />
                  </div>
                  <div className="absolute -top-2 -left-2 w-7 h-7 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md">
                    2
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">Completez votre profil</h3>
                  <p className="text-sm text-gray-600">
                    Ajoutez votre date de naissance pour des analyses personnalisees
                  </p>
                </div>
                <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2">
                  <ArrowRight className="h-5 w-5 text-amber-400" />
                </div>
              </div>
              
              {/* Step 3 */}
              <div className="relative">
                <div className="flex flex-col items-center text-center p-4 bg-white/70 rounded-2xl border border-amber-100 hover:border-amber-300 hover:shadow-md transition-all">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-purple-500 rounded-full flex items-center justify-center mb-3 shadow-sm">
                    <Wand2 className="h-6 w-6 text-white" />
                  </div>
                  <div className="absolute -top-2 -left-2 w-7 h-7 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md">
                    3
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">Explorez les apps</h3>
                  <p className="text-sm text-gray-600">
                    Chiromancie, Tarot, Astrologie ou Numerologie - a vous de choisir
                  </p>
                </div>
                <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2">
                  <ArrowRight className="h-5 w-5 text-amber-400" />
                </div>
              </div>
              
              {/* Step 4 */}
              <div className="relative">
                <div className="flex flex-col items-center text-center p-4 bg-white/70 rounded-2xl border border-amber-100 hover:border-amber-300 hover:shadow-md transition-all">
                  <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-emerald-500 rounded-full flex items-center justify-center mb-3 shadow-sm">
                    <Sparkles className="h-6 w-6 text-white" />
                  </div>
                  <div className="absolute -top-2 -left-2 w-7 h-7 bg-emerald-500 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md">
                    4
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">Recevez vos revelations</h3>
                  <p className="text-sm text-gray-600">
                    L'IA analyse et vous offre des insights personnalises
                  </p>
                </div>
              </div>
            </div>
            
            {/* Call to action for non-authenticated users */}
            {!isAuthenticated && (
              <div className="mt-6 text-center">
                <Button
                  onClick={() => navigate('/auth')}
                  className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-8 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all"
                  data-testid="how-it-works-cta"
                >
                  <Sparkles className="h-5 w-5 mr-2" />
                  Commencer gratuitement
                  <ArrowRight className="h-5 w-5 ml-2" />
                </Button>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Personal Dashboard (for authenticated users with birth data) */}
      <div className="max-w-6xl mx-auto px-4">
        <PersonalDashboard />
      </div>

      {/* Transversal Analysis Banner (Premium Feature) */}
      {isAuthenticated && user?.is_premium && (
        <div className="max-w-6xl mx-auto px-4 mb-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Link
              to="/analysis/transversal"
              className="block bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 rounded-2xl p-6 hover:shadow-2xl hover:shadow-purple-500/20 transition-all group"
              data-testid="transversal-analysis-banner"
            >
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                    <Activity className="h-7 w-7 text-white" />
                  </div>
                  <div className="flex items-center gap-2">
                    <Sun className="h-5 w-5 text-amber-300" />
                    <Hash className="h-5 w-5 text-emerald-300" />
                    <Moon className="h-5 w-5 text-indigo-200" />
                  </div>
                </div>
                <div className="text-center md:text-left flex-1">
                  <div className="flex items-center gap-2 justify-center md:justify-start mb-1">
                    <h3 className="font-bold text-white text-lg">Analyse Transversale</h3>
                    <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs text-white/90">NOUVEAU</span>
                  </div>
                  <p className="text-purple-100 text-sm">
                    Vision holistique combinant astrologie, numerologie et cycles personnels avec graphiques interactifs
                  </p>
                </div>
                <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-xl group-hover:bg-white/30 transition-colors">
                  <Compass className="h-5 w-5 text-white" />
                  <span className="text-white font-medium">Decouvrir</span>
                  <ArrowRight className="h-4 w-4 text-white group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>
          </motion.div>
        </div>
      )}

      {/* Playlist & Premium Banner */}
      <div className="max-w-6xl mx-auto px-4 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-4"
        >
          {/* Playlist Banner */}
          <div className="bg-gradient-to-r from-violet-500/10 via-purple-500/10 to-indigo-500/10 rounded-2xl border border-purple-200 p-5 flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
              <Music className="h-6 w-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 mb-1">Playlists Ambient</h3>
              <p className="text-sm text-gray-600">
                Activez la musique pour une experience immersive dans chaque application
              </p>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 bg-white/80 rounded-xl border border-purple-200">
              <Volume2 className="h-4 w-4 text-purple-500" />
              <span className="text-xs text-purple-600 font-medium">4 playlists</span>
            </div>
          </div>
          
          {/* Premium Banner */}
          <Link
            to="/premium"
            className="bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-rose-500/10 rounded-2xl border border-amber-200 p-5 flex items-center gap-4 hover:border-amber-300 hover:shadow-lg transition-all group"
            data-testid="premium-banner"
          >
            <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-rose-500 rounded-xl flex items-center justify-center flex-shrink-0">
              <Crown className="h-6 w-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 mb-1">Passez Premium</h3>
              <p className="text-sm text-gray-600">
                Debloquez tout REVELIAE pour 9.99EUR/mois ou par application
              </p>
            </div>
            <ArrowRight className="h-5 w-5 text-amber-500 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>
      </div>

      {/* Products Grid */}
      <div className="max-w-6xl mx-auto px-4 pb-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {PRODUCTS.map((product, index) => (
            <ProductCard
              key={product.id}
              product={product}
              index={index}
            />
          ))}
        </div>
      </div>

      {/* Testimonials Section */}
      <TestimonialsSection isAuthenticated={isAuthenticated} user={user} />

      {/* Footer Note */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
        className="text-center pb-12 px-4"
      >
        <p className="text-gray-600 text-sm">
          Un produit de <span className="text-amber-600 font-medium">C4U Aventures</span>
        </p>
        <p className="text-gray-400 text-xs mt-2">
          © 2026 REVELIAE • Arts divinatoires par IA
        </p>
      </motion.div>

      {/* Playlist Toggle */}
      <PlaylistToggle theme="amber" />
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default ReveliaePage;
